import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Smartphone, Wrench, FileText, Loader2, ArrowUpRight, X, Clock, Package, Users, Boxes } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECENT_MAX = 5;
const RECENT_KEY_PREFIX = 'gs:recent:';

function loadRecent(userKey: string): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY_PREFIX + userKey);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function saveRecent(userKey: string, list: string[]) {
  try {
    localStorage.setItem(RECENT_KEY_PREFIX + userKey, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    // localStorage may throw in private mode / quota exceeded — safe to ignore.
  }
}

/** Global cross-entity search — top-nav dropdown that jumps to a device,
 *  repair, or quote. Keyboard shortcuts: `/` or `⌘K` to focus, `Esc` to
 *  close, `Enter` to open the top result, `↑/↓` to move selection.
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userKey = user?.email || user?.user_id || 'anon';
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any>({ devices: [], repairs: [], quotes: [], batches: [], customers: [], parts: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [recent, setRecent] = useState<string[]>([]);

  // Load per-user recent list on mount / user change.
  useEffect(() => {
    setRecent(loadRecent(userKey));
  }, [userKey]);

  const pushRecent = useCallback((q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) return;
    setRecent((prev) => {
      const next = [trimmed, ...prev.filter((x) => x.toLowerCase() !== trimmed.toLowerCase())].slice(0, RECENT_MAX);
      saveRecent(userKey, next);
      return next;
    });
  }, [userKey]);

  const removeRecent = useCallback((q: string) => {
    setRecent((prev) => {
      const next = prev.filter((x) => x !== q);
      saveRecent(userKey, next);
      return next;
    });
  }, [userKey]);

  const clearRecent = useCallback(() => {
    setRecent([]);
    saveRecent(userKey, []);
  }, [userKey]);

  // Flatten results into a single navigable list for keyboard nav.
  const flat: { type: 'device' | 'repair' | 'quote' | 'batch' | 'customer' | 'part'; item: any; path: string }[] = [
    ...(results.devices || []).map((d: any) => ({ type: 'device' as const, item: d, path: `/devices/${d.device_id}` })),
    ...(results.repairs || []).map((r: any) => ({ type: 'repair' as const, item: r, path: `/repairs/${r.repair_id}` })),
    ...(results.quotes || []).map((q: any) => ({ type: 'quote' as const, item: q, path: `/deals/${q.quote_id}` })),
    ...(results.batches || []).map((b: any) => ({ type: 'batch' as const, item: b, path: `/batches/${b.batch_id}` })),
    ...(results.customers || []).map((c: any) => ({ type: 'customer' as const, item: c, path: `/customers/${c.customer_id}` })),
    ...(results.parts || []).map((p: any) => ({ type: 'part' as const, item: p, path: `/parts` })),
  ];

  // Debounced fetch — 180ms is short enough to feel instant but avoids
  // spamming the API on every keystroke.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults({ devices: [], repairs: [], quotes: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search`, { params: { q, limit: 6 } });
        setResults(res.data || { devices: [], repairs: [], quotes: [] });
        setSelectedIdx(0);
      } catch (e) {
        setResults({ devices: [], repairs: [], quotes: [] });
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query]);

  // Global hotkey: `/` or `⌘K` / `Ctrl+K` focuses the search input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const inField = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t as any).isContentEditable);
      if ((e.key === '/' && !inField) || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Click outside → close dropdown.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const gotoResult = useCallback((r: typeof flat[number]) => {
    // Remember this query — the tech is committing to it by opening a result.
    pushRecent(query);
    setOpen(false);
    setQuery('');
    // Blur so the `/` hotkey works on the next page instead of typing `/` into
    // the still-focused input.
    inputRef.current?.blur();
    navigate(r.path);
  }, [navigate, pushRecent, query]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const target = flat[selectedIdx];
      if (target) {
        e.preventDefault();
        gotoResult(target);
      }
    }
  };

  const hasResults = flat.length > 0;
  const trimmedQ = query.trim();
  const showRecent = open && trimmedQ.length < 2 && recent.length > 0;
  const showResults = open && trimmedQ.length >= 2;

  return (
    <div className="relative flex-1 max-w-md" ref={containerRef} data-testid="global-search-wrapper">
      <div className="relative flex items-center">
        <Search size={14} className="absolute left-2.5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search IMEI, device, repair, deal…"
          className="w-full pl-8 pr-16 py-1.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark-topbar-input"
          data-testid="global-search-input"
        />
        <div className="absolute right-2 flex items-center gap-1 text-[10px] text-gray-400 pointer-events-none">
          {loading ? (
            <Loader2 size={12} className="animate-spin" />
          ) : query ? (
            <button
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              className="pointer-events-auto p-0.5 rounded hover:bg-gray-100"
              data-testid="global-search-clear"
            >
              <X size={12} />
            </button>
          ) : (
            <>
              <kbd className="px-1 py-0.5 rounded border bg-gray-50">/</kbd>
              <span>or</span>
              <kbd className="px-1 py-0.5 rounded border bg-gray-50">⌘K</kbd>
            </>
          )}
        </div>
      </div>

      {showRecent && (
        <div
          className="absolute top-11 left-0 right-0 bg-white rounded-lg shadow-2xl border z-50 max-h-[70vh] overflow-y-auto animate-fade-in"
          data-testid="global-search-recent"
        >
          <div className="px-3 py-1.5 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center justify-between border-b">
            <span className="flex items-center gap-1.5"><Clock size={11} /> Recent searches</span>
            <button
              onClick={clearRecent}
              className="text-[10px] font-semibold text-gray-500 hover:text-rose-600"
              data-testid="global-search-recent-clear"
            >
              Clear all
            </button>
          </div>
          <div>
            {recent.map((q, i) => (
              <div
                key={q + i}
                className="flex items-center gap-2 px-3 py-2 hover:bg-teal-50 group cursor-pointer"
                onClick={() => { setQuery(q); inputRef.current?.focus(); }}
                data-testid={`global-search-recent-item-${i}`}
              >
                <Clock size={12} className="text-gray-400 shrink-0" />
                <span className="flex-1 truncate text-sm text-gray-800">{q}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeRecent(q); }}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 text-gray-400"
                  data-testid={`global-search-recent-remove-${i}`}
                  title="Remove from recents"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
          <div className="px-3 py-1.5 border-t bg-gray-50 text-[10px] text-gray-500">
            Type to search · <kbd className="px-1 border rounded">Esc</kbd> to close
          </div>
        </div>
      )}

      {showResults && (
        <div
          className="absolute top-11 left-0 right-0 bg-white rounded-lg shadow-2xl border z-50 max-h-[70vh] overflow-y-auto animate-fade-in"
          data-testid="global-search-dropdown"
        >
          {!hasResults && !loading && (
            <div className="p-4 text-sm text-gray-500 text-center" data-testid="global-search-empty">
              No matches for &ldquo;{query}&rdquo;
            </div>
          )}
          {loading && !hasResults && (
            <div className="p-4 text-sm text-gray-400 text-center flex items-center gap-2 justify-center">
              <Loader2 size={14} className="animate-spin" /> Searching…
            </div>
          )}

          {results.devices?.length > 0 && (
            <ResultGroup label="Devices" icon={Smartphone}>
              {results.devices.map((d: any, i: number) => (
                <ResultRow
                  key={d.device_id}
                  selected={selectedIdx === i}
                  onClick={() => gotoResult({ type: 'device', item: d, path: `/devices/${d.device_id}` })}
                  data-testid={`gs-device-${d.device_id}`}
                  title={d.device_id}
                  meta={`${d.model || 'Unknown'} · ${d.storage_gb || '—'}GB · ${d.colour || '—'}`}
                  right={<span className="text-[10px] font-mono text-gray-500">{d.imei}</span>}
                  status={d.status}
                  chips={d.is_unexpected ? [{ label: 'UNEXPECTED', cls: 'bg-purple-100 text-purple-800' }] : []}
                />
              ))}
            </ResultGroup>
          )}

          {results.repairs?.length > 0 && (
            <ResultGroup label="Repairs" icon={Wrench}>
              {results.repairs.map((r: any, i: number) => (
                <ResultRow
                  key={r.repair_id}
                  selected={selectedIdx === (results.devices?.length || 0) + i}
                  onClick={() => gotoResult({ type: 'repair', item: r, path: `/repairs/${r.repair_id}` })}
                  data-testid={`gs-repair-${r.repair_id}`}
                  title={r.repair_id}
                  meta={`${r.device_id || '—'} · ${r.queue_type ? r.queue_type.replace('_', ' & ') : 'queue —'}`}
                  status={r.status}
                  chips={r.is_unexpected ? [{ label: 'UNEXPECTED', cls: 'bg-purple-100 text-purple-800' }] : []}
                />
              ))}
            </ResultGroup>
          )}

          {results.quotes?.length > 0 && (
            <ResultGroup label="Deals / Quotes" icon={FileText}>
              {results.quotes.map((q: any, i: number) => (
                <ResultRow
                  key={q.quote_id}
                  selected={selectedIdx === (results.devices?.length || 0) + (results.repairs?.length || 0) + i}
                  onClick={() => gotoResult({ type: 'quote', item: q, path: `/deals/${q.quote_id}` })}
                  data-testid={`gs-quote-${q.quote_id}`}
                  title={q.quote_id}
                  meta={q.customer_name || 'No customer'}
                  right={q.total_zar ? <span className="text-[11px] text-gray-500">R{Number(q.total_zar).toLocaleString()}</span> : null}
                  status={q.status}
                />
              ))}
            </ResultGroup>
          )}

          {results.batches?.length > 0 && (
            <ResultGroup label="Batches" icon={Boxes}>
              {results.batches.map((b: any, i: number) => {
                const base = (results.devices?.length || 0) + (results.repairs?.length || 0) + (results.quotes?.length || 0);
                return (
                  <ResultRow
                    key={b.batch_id}
                    selected={selectedIdx === base + i}
                    onClick={() => gotoResult({ type: 'batch', item: b, path: `/batches/${b.batch_id}` })}
                    data-testid={`gs-batch-${b.batch_id}`}
                    title={b.batch_id}
                    meta={`${b.supplier_name || 'Unknown supplier'} · ${b.total_devices ?? 0} devices`}
                    status={b.status}
                  />
                );
              })}
            </ResultGroup>
          )}

          {results.customers?.length > 0 && (
            <ResultGroup label="Customers" icon={Users}>
              {results.customers.map((c: any, i: number) => {
                const base = (results.devices?.length || 0) + (results.repairs?.length || 0) + (results.quotes?.length || 0) + (results.batches?.length || 0);
                return (
                  <ResultRow
                    key={c.customer_id}
                    selected={selectedIdx === base + i}
                    onClick={() => gotoResult({ type: 'customer', item: c, path: `/customers/${c.customer_id}` })}
                    data-testid={`gs-customer-${c.customer_id}`}
                    title={c.company_name || c.customer_id}
                    meta={`${c.contact_name || '—'} · ${c.email || '—'}`}
                  />
                );
              })}
            </ResultGroup>
          )}

          {results.parts?.length > 0 && (
            <ResultGroup label="Parts" icon={Package}>
              {results.parts.map((p: any, i: number) => {
                const base = (results.devices?.length || 0) + (results.repairs?.length || 0) + (results.quotes?.length || 0) + (results.batches?.length || 0) + (results.customers?.length || 0);
                const lowStock = (p.stock_qty ?? 0) <= (p.min_stock_level ?? 0);
                return (
                  <ResultRow
                    key={p.part_id}
                    selected={selectedIdx === base + i}
                    onClick={() => gotoResult({ type: 'part', item: p, path: `/parts` })}
                    data-testid={`gs-part-${p.part_id}`}
                    title={p.name || p.sku || p.part_id}
                    meta={`${p.sku ? `SKU ${p.sku}` : p.part_id} · stock ${p.stock_qty ?? 0}`}
                    chips={lowStock ? [{ label: 'LOW STOCK', cls: 'bg-amber-100 text-amber-800' }] : []}
                  />
                );
              })}
            </ResultGroup>
          )}

          <div className="px-3 py-2 border-t bg-gray-50 text-[10px] text-gray-500 flex items-center justify-between">
            <span>↑↓ to navigate · <kbd className="px-1 border rounded">Enter</kbd> to open · <kbd className="px-1 border rounded">Esc</kbd> to close</span>
            <span>{flat.length} result{flat.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultGroup({ label, icon: Icon, children }: any) {
  return (
    <div className="border-b last:border-b-0">
      <div className="px-3 py-1.5 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ResultRow({ title, meta, right, status, chips, selected, onClick, ...rest }: any) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-teal-50 transition ${selected ? 'bg-teal-50 border-l-2 border-teal-500' : ''}`}
      {...rest}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-gray-900 truncate">{title}</span>
          {(chips || []).map((c: any) => (
            <span key={c.label} className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${c.cls}`}>{c.label}</span>
          ))}
          {status && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 text-gray-700">{status}</span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 truncate">{meta}</div>
      </div>
      {right && <div className="shrink-0">{right}</div>}
      <ArrowUpRight size={12} className="text-gray-400 shrink-0" />
    </button>
  );
}
